import type { GoodCustomsInformation, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import {
  getCommodityAssignedParentIndex,
  isCommodityParentAssigned,
  projectCommodityRows,
} from "@RequestFormV2/sections/PackingList/utils/commodityProjection";
import { isCommodityRowEmpty, isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

export type ReviewPackingListTableRow = {
  index: number;
  rowNumber: number;
  row: PackingListRow;
  commodities: GoodCustomsInformation[];
};

function isReviewTableRow(row: PackingListRow | undefined) {
  if (!row || isPackingListRowEmpty(row)) return false;
  return isContainerRow(row) || Boolean(row.handling_unit);
}

function buildCommoditiesByParentIndex(packingList: PackingListRow[]): Map<number, GoodCustomsInformation[]> {
  const commoditiesByParent = new Map<number, GoodCustomsInformation[]>();

  projectCommodityRows(packingList).forEach(({ packingListIndex, parentRow, commodity }) => {
    if (!isCommodityParentAssigned(commodity, parentRow)) return;
    if (isCommodityRowEmpty(commodity)) return;

    const parentIndex = getCommodityAssignedParentIndex(packingListIndex, commodity);
    const existing = commoditiesByParent.get(parentIndex) ?? [];
    existing.push(commodity);
    commoditiesByParent.set(parentIndex, existing);
  });

  return commoditiesByParent;
}

export function buildReviewPackingListRows(
  packingList: PackingListRow[] | undefined,
): ReviewPackingListTableRow[] {
  const rows = packingList ?? [];
  const commoditiesByParent = buildCommoditiesByParentIndex(rows);
  const tableRows: ReviewPackingListTableRow[] = [];
  let rowNumber = 0;

  rows.forEach((row, index) => {
    if (!isReviewTableRow(row)) return;

    rowNumber += 1;
    tableRows.push({
      index,
      rowNumber,
      row,
      commodities: commoditiesByParent.get(index) ?? [],
    });
  });

  return tableRows;
}

export function hasReviewPackingListRows(packingList: PackingListRow[] | undefined): boolean {
  return buildReviewPackingListRows(packingList).length > 0;
}
